import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../utils/firebase-config';
import Footer from '../Components/Footer';
import Breadcrumbs from '../Components/Breadcrumbs';

const HEADINGS = { resetPassword: 'Choose a new password', verifyEmail: 'Confirm your email' };

/**
 * Opens the links in SoapLab's account emails: confirming an email address and resetting a password.
 */
export default function AuthActionPage() {
  const [params] = useSearchParams();
  const mode = params.get('mode');
  const oobCode = params.get('oobCode');
  const supported = Boolean(oobCode && HEADINGS[mode]);
  const [status, setStatus] = useState(supported ? 'checking' : 'invalid'); // checking | ready | saving | done | invalid
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  // Codes work once; keep the first attempt so a re-run effect does not spend it again.
  const attempt = useRef(null);

  useEffect(() => {
    document.title = `${HEADINGS[mode] || 'Account link'} | SoapLab`;
    if (!supported) return undefined;
    let active = true;
    attempt.current = attempt.current || (mode === 'resetPassword'
      ? verifyPasswordResetCode(auth, oobCode)
      : applyActionCode(auth, oobCode).then(async () => {
        if (auth.currentUser) {
          await auth.currentUser.reload();
          await auth.currentUser.getIdToken(true);
        }
      }));
    attempt.current
      .then(address => {
        if (!active) return;
        if (mode === 'resetPassword') setEmail(address);
        setStatus(mode === 'resetPassword' ? 'ready' : 'done');
      })
      .catch(async () => {
        // A confirmation link opened a second time fails, but the address is already confirmed.
        if (mode === 'verifyEmail' && auth.currentUser) await auth.currentUser.reload().catch(() => {});
        if (active) setStatus(mode === 'verifyEmail' && auth.currentUser?.emailVerified ? 'done' : 'invalid');
      });
    return () => { active = false; };
  }, [mode, oobCode, supported]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords don’t match.');
      return;
    }
    setStatus('saving');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('done');
    } catch (err) {
      if (['auth/expired-action-code', 'auth/invalid-action-code'].includes(err.code)) {
        setStatus('invalid');
        return;
      }
      setError(['auth/weak-password', 'auth/password-does-not-meet-requirements'].includes(err.code)
        ? 'Please choose a stronger password.'
        : 'We couldn’t save your new password. Please try again.');
      setStatus('ready');
    }
  };

  const heading = status !== 'invalid' ? HEADINGS[mode] : supported ? 'This link has expired' : 'Link not valid';
  const linkMode = supported ? mode : null;

  return (
    <div className="sl-page auth-page">
      <div className="pageheader borderleft">
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: heading }]} />
        <div className="pageheading">{heading}</div>
      </div>

      <div className="auth-content borderleft">
        <div className="auth-container">
          {status === 'checking' && <p className="auth-subtitle">Checking your link…</p>}

          {status === 'invalid' && (
            <>
              <div className="auth-error">
                <span>⚠️</span>
                {linkMode === 'resetPassword'
                  ? 'This password reset link has expired or has already been used.'
                  : linkMode === 'verifyEmail'
                    ? 'This confirmation link has expired or has already been used.'
                    : 'This link isn’t valid. Please use the button in the latest email we sent you.'}
              </div>
              {linkMode === 'resetPassword' && (
                <p className="auth-subtitle">Go to sign in and choose “Forgot password?” to get a new link.</p>
              )}
              {linkMode === 'verifyEmail' && (
                <p className="auth-subtitle">Sign in and we’ll offer to send you a new confirmation email.</p>
              )}
              <Link to={linkMode === 'verifyEmail' && auth.currentUser ? '/verify-email' : '/login'} className="auth-submit-btn auth-link-btn">
                {linkMode === 'verifyEmail' && auth.currentUser ? 'Send a new confirmation email' : 'Go to sign in'}
              </Link>
            </>
          )}

          {mode === 'resetPassword' && ['ready', 'saving'].includes(status) && (
            <>
              <p className="auth-subtitle">for {email}</p>
              {error && (
                <div className="auth-error">
                  <span>⚠️</span> {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label htmlFor="new-password">New password</label>
                  <input
                    type="password"
                    id="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    minLength={6}
                    required
                  />
                  <span className="form-hint">At least 6 characters</span>
                </div>
                <div className="form-group">
                  <label htmlFor="confirm-password">Confirm new password</label>
                  <input
                    type="password"
                    id="confirm-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    minLength={6}
                    required
                  />
                </div>
                <button type="submit" className="auth-submit-btn" disabled={status === 'saving'}>
                  {status === 'saving' ? 'Saving…' : 'Save new password'}
                </button>
              </form>
            </>
          )}

          {status === 'done' && (
            <>
              <div className="auth-success">
                <span>✅</span>
                {mode === 'resetPassword'
                  ? 'Your password has been changed. You can now sign in with your new password.'
                  : 'Your email address is confirmed. Welcome to SoapLab!'}
              </div>
              {mode === 'verifyEmail' && auth.currentUser ? (
                <Link to="/" className="auth-submit-btn auth-link-btn">Go to your dashboard</Link>
              ) : (
                <Link to="/login" className="auth-submit-btn auth-link-btn">Sign in</Link>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
